// src/authpages/group/ActiveGroups.js
import React from 'react';
import { 
  Users, 
  UserCheck,
  MessageSquare,
  Video,
  Clock,
  Plus,
  Loader,
  Edit
} from 'lucide-react';

const ActiveGroups = ({ 
  activeGroups, 
  currentUserId, 
  getFriendNameById, 
  joinGroup, 
  onCreateClick,
  onGroupSelect,
  onEditGroup,
  loading,
  error
}) => {
  return (
    <div className="bg-gray-50 rounded-lg p-6">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800 flex items-center">
        <Users className="mr-3 text-green-500" /> Active Study Groups
      </h2>
      
      {loading ? (
        <div className="text-center py-10">
          <Loader className="mx-auto h-8 w-8 text-blue-500 animate-spin mb-3" />
          <p className="text-gray-600">Loading your study groups...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md text-red-600 mb-4">
          {error}
        </div>
      ) : activeGroups.length > 0 ? (
        <div className="space-y-4">
          {activeGroups.map((group) => (
            <div 
              key={group.id} 
              className="bg-white rounded-lg p-4 shadow-md"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg">{group.name}</h3>
                <div className="text-sm text-gray-500">
                  {group.memberCount}/{group.maxMembers} members
                </div>
              </div>
              
              <div className="mb-3">
                <p className="text-gray-600">
                  <span className="font-medium">Subject:</span> {group.subject}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Group Study Method:</span> {group.studyMethod}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Timer Method:</span> {group.timerMethod}
                </p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg mb-3">
                <p className="text-sm font-medium text-blue-800 mb-1">Group Members</p>
                <div className="flex flex-wrap gap-2">
                  {group.members.map((memberId, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      <UserCheck className="mr-1" size={14} />
                      {memberId === currentUserId ? 'You' : getFriendNameById(memberId)}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                {group.members.includes(currentUserId) ? (
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => onGroupSelect(group)}
                      className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 flex items-center"
                    >
                      <Video className="mr-2" size={18} />
                      Start Session
                    </button>
                    
                    {/* Add Edit button if user is the creator */}
                    {group.creator === currentUserId && (
                      <button 
                        onClick={() => onEditGroup(group)}
                        className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center"
                      >
                        <Edit className="mr-2" size={18} />
                        Edit Group
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex space-x-2">
                    <button 
                      className="bg-green-100 text-green-700 px-3 py-1 rounded-md flex items-center text-sm hover:bg-green-200 opacity-50 cursor-not-allowed"
                      disabled
                    >
                      <MessageSquare className="mr-1" size={16} />
                      Chat
                    </button>
                    <button 
                      className="bg-blue-100 text-blue-700 px-3 py-1 rounded-md flex items-center text-sm hover:bg-blue-200 opacity-50 cursor-not-allowed"
                      disabled
                    >
                      <Video className="mr-1" size={16} />
                      Video
                    </button>
                    <button 
                      className="bg-purple-100 text-purple-700 px-3 py-1 rounded-md flex items-center text-sm hover:bg-purple-200 opacity-50 cursor-not-allowed"
                      disabled
                    >
                      <Clock className="mr-1" size={16} />
                      Timer
                    </button>
                  </div>
                )}
                
                {!group.members.includes(currentUserId) && (
                  <button 
                    onClick={() => joinGroup(group.id)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50"
                    disabled={group.memberCount >= group.maxMembers || loading}
                  >
                    {group.memberCount >= group.maxMembers ? 'Full' : loading ? 'Joining...' : 'Join Group'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Users className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-gray-600 mb-2">
            No active groups. Create a new group to get started!
          </p>
          <button 
            onClick={onCreateClick}
            className="mt-2 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          >
            <Plus className="inline mr-1" size={18} />
            Create New Group
          </button>
        </div>
      )}
    </div>
  );
};

export default ActiveGroups;